import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../../../components/header/header";
import TrafficLightIndicatorOverall from "../../../components/results/traficLight/trafficLightIndicatorOverall";
import TrafficLightIndicatorNutrition from "../../../components/results/traficLight/trafficLightIndicatorNutrition";
import TrafficLightIndicatorWork from "../../../components/results/traficLight/trafficLightIndicatorWork";
import TrafficLightIndicatorMetabolic from "../../../components/results/traficLight/trafficLightIndicatorMetabolic";
import TrafficLightIndicatorActivity from "../../../components/results/traficLight/trafficLightIndicatorActivity";
import TrafficLightIndicatorLifeStyle from "../../../components/results/traficLight/trafficLightIndicatorLifeStyle";
import recommendationsApi from "../../../api/recommendationsApi";
import { createPdfBlobUrl } from "../../../utils/createPdf";
import { getSummaryBox } from "../../../utils/getSummaryBox";
import "./hraResultPage.css";

const HraResultPage = () => {
  const { state } = useLocation();
  const results = state?.response;
  const [pdfLoading, setPdfLoading] = useState(false);
  const [recommendations, setRecommendations] = useState({});
  const [loadingRecs, setLoadingRecs] = useState(true);

  useEffect(() => {
    if (!results) return;

    const fetchAllRecs = async () => {
      const keys = [
        { key: "overallEvaluation", section: "overall", level: results.overallEvaluation },
        { key: "nutritionalState", section: "nutrition", level: results.nutritionalState },
        { key: "workHappiness", section: "work", level: results.workHappiness },
        { key: "metabolicRisk", section: "metabolic", level: results.metabolicRisk },
        { key: "physicalActivityEvaluation", section: "activity", level: results.physicalActivityEvaluation },
        { key: "lifeStyle", section: "lifestyle", level: results.lifeStyle },
      ];

      const temp = {};
      for (const { key, section, level } of keys) {
        try {
          const res = await recommendationsApi.getBySectionAndLevel(section, level);
          let text = res.text;
          if (section === "metabolic" && level > 0 && results.metabolicRiskCauses) {
            text = text.replace("{{FACTORES_DE_RIESGO}}", results.metabolicRiskCauses);
          }
          temp[key] = text;
        } catch (err) {
          temp[key] = "No se pudo cargar la recomendación.";
        }
      }

      setRecommendations(temp);
      setLoadingRecs(false);
    };

    fetchAllRecs();
  }, [results]);

  if (!results) return <h2>No hay datos disponibles</h2>;
  if (loadingRecs) return <h2>Cargando recomendaciones...</h2>;

  const recs = [
    { key: "overallEvaluation", title: "Evaluación General", text: recommendations.overallEvaluation },
    { key: "nutritionalState", title: "Índice de Masa Corporal", text: recommendations.nutritionalState },
    { key: "workHappiness", title: "Felicidad en el trabajo", text: recommendations.workHappiness },
    { key: "metabolicRisk", title: "Riesgo Metabólico", text: recommendations.metabolicRisk },
    { key: "physicalActivityEvaluation", title: "Nivel de sedentarismo", text: recommendations.physicalActivityEvaluation },
    { key: "lifeStyle", title: "Estilo de vida", text: recommendations.lifeStyle }
  ];

  const summaryBoxes = [
    getSummaryBox("overall", results.overallEvaluation),
    getSummaryBox("nutrition", results.nutritionalState),
    getSummaryBox("work", results.workHappiness),
    getSummaryBox("metabolic", results.metabolicRisk),
    getSummaryBox("activity", results.physicalActivityEvaluation),
    getSummaryBox("lifestyle", results.lifeStyle)
  ];

  const handleRecommendationClick = (key) => {
    const detail = document.getElementById(`rec-${key}`);
    if (detail) {
      detail.open = true;
      detail.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePrintPdf = () => {
    setPdfLoading(true);

    setTimeout(() => {
      const blobUrl = createPdfBlobUrl(recs, summaryBoxes);
      const pdfWindow = window.open(blobUrl, "_blank");

      if (pdfWindow) {
        const onLoad = () => setPdfLoading(false);
        pdfWindow.addEventListener("load", onLoad);
        setTimeout(() => setPdfLoading(false), 3000);
      } else {
        setPdfLoading(false);
        alert("No se pudo abrir la pestaña de PDF. Verifica tu bloqueador de ventanas emergentes.");
      }
    }, 100);
  };

  return (
    <div className="hra-result-page">
      <Header />

      <button
        className="print-button"
        onClick={handlePrintPdf}
        disabled={pdfLoading}
      >
        {pdfLoading ? (
          <>
            <div className="spinner" /> Generando PDF...
          </>
        ) : (
          "Abrir PDF"
        )}
      </button>

      <section className="result-header">
        <h1>Health Risk Assessment</h1>
        <p>Revisa tus indicadores de salud y estilo de vida</p>
      </section>

      <section className="result-summary card">
        <h2>Resumen General</h2>
        <TrafficLightIndicatorOverall
          category="Evaluación General"
          riskLevel={results.overallEvaluation}
          recommendation=""
        />
        <button
          className="reco-link"
          onClick={() => handleRecommendationClick("overallEvaluation")}
        >
          Ver recomendación
        </button>
      </section>

      <section className="metrics-grid">
        <div className="card metric-card">
          <h3>Índice de Masa Corporal</h3>
          <TrafficLightIndicatorNutrition
            category={results.imc}
            riskLevel={results.nutritionalState}
            recommendation=""
          />
          <button
            className="reco-link"
            onClick={() => handleRecommendationClick("nutritionalState")}
          >
            Ver recomendación
          </button>
        </div>

        <div className="card metric-card">
          <h3>Felicidad en el trabajo</h3>
          <TrafficLightIndicatorWork
            category="Felicidad en el Trabajo"
            riskLevel={results.workHappiness}
            recommendation=""
          />
          <button
            className="reco-link"
            onClick={() => handleRecommendationClick("workHappiness")}
          >
            Ver recomendación
          </button>
        </div>

        <div className="card metric-card">
          <h3>Riesgo Metabólico</h3>
          <TrafficLightIndicatorMetabolic
            category="Metabólico"
            riskLevel={results.metabolicRisk}
            recommendation=""
          />
          <button
            className="reco-link"
            onClick={() => handleRecommendationClick("metabolicRisk")}
          >
            Ver recomendación
          </button>
        </div>

        <div className="card metric-card">
          <h3>Sedentarismo</h3>
          <TrafficLightIndicatorActivity
            category="Sedentarismo"
            riskLevel={results.physicalActivityEvaluation}
            recommendation=""
          />
          <button
            className="reco-link"
            onClick={() => handleRecommendationClick("physicalActivityEvaluation")}
          >
            Ver recomendación
          </button>
        </div>

        <div className="card metric-card">
          <h3>Estilo de vida</h3>
          <TrafficLightIndicatorLifeStyle
            category="Estilo Vida"
            riskLevel={results.lifeStyle}
            recommendation=""
          />
          <button
            className="reco-link"
            onClick={() => handleRecommendationClick("lifeStyle")}
          >
            Ver recomendación
          </button>
        </div>
      </section>

      <section className="recommendations-section card">
        <h2>Recomendaciones por área</h2>
        {recs.map(({ key, title, text }) => (
          <details key={key} id={`rec-${key}`} className="reco-collapse">
            <summary>{title}</summary>
            <p>{text}</p>
          </details>
        ))}
      </section>
    </div>
  );
};

export default HraResultPage;
